import Phaser from 'phaser';
import type { MapData } from '@/types/map-data';
import { COLORS } from '@/config/colors';
import { tileToPixelX, tileToPixelY, isWalkable } from '@/maps/map-renderer';

type Direction = 'left' | 'right' | 'up' | 'down' | 'none';

const PACMAN_RADIUS = 7;
const MOVE_SPEED = 120;
const PRE_TURN_TOLERANCE_PX = 6;
const CORNER_RECOVERY_PX = 4;

/** Maximum mouth opening angle (radians). PI/3 = 60° total gap. */
const MAX_MOUTH_ANGLE = Math.PI / 3;
/** Speed of mouth animation (radians/second). Higher = faster chomp cycle. */
const MOUTH_ANIM_SPEED = 12;
/** Idle mouth chomp speed (slower gentle pulse when not moving). */
const MOUTH_IDLE_SPEED = 4;

interface DirectionDelta {
  readonly dx: number;
  readonly dy: number;
}

const DIRECTION_DELTAS: Record<Exclude<Direction, 'none'>, DirectionDelta> = {
  left: { dx: -1, dy: 0 },
  right: { dx: 1, dy: 0 },
  up: { dx: 0, dy: -1 },
  down: { dx: 0, dy: 1 },
} as const;

export class PacmanEntity {
  private readonly mapData: MapData;
  private readonly graphics: Phaser.GameObjects.Graphics;
  private readonly globalSpeedMultiplier: number;

  private tileX: number;
  private tileY: number;
  private pixelX: number;
  private pixelY: number;
  private currentDirection: Direction = 'none';
  private nextDirection: Direction = 'none';
  private moving = false;
  private targetPixelX: number;
  private targetPixelY: number;
  private prevTileX: number;
  private prevTileY: number;
  private isTunneling = false;
  /** Continuous phase for mouth animation (used with sin for smooth oscillation). */
  private mouthPhase = 0;
  private deathAnimationActive = false;
  private deathAnimationProgress = 0;

  constructor(scene: Phaser.Scene, mapData: MapData, globalSpeedMultiplier = 1) {
    this.mapData = mapData;
    this.globalSpeedMultiplier = globalSpeedMultiplier;
    this.tileX = mapData.pacmanSpawn.x;
    this.tileY = mapData.pacmanSpawn.y;
    this.pixelX = tileToPixelX(this.tileX);
    this.pixelY = tileToPixelY(this.tileY);
    this.targetPixelX = this.pixelX;
    this.targetPixelY = this.pixelY;
    this.prevTileX = this.tileX;
    this.prevTileY = this.tileY;
    this.graphics = scene.add.graphics();
    this.draw();
  }

  setDirection(direction: Direction): void {
    this.nextDirection = direction;
  }

  getTileX(): number {
    return this.tileX;
  }

  getTileY(): number {
    return this.tileY;
  }

  getPixelX(): number {
    return this.pixelX;
  }

  getPixelY(): number {
    return this.pixelY;
  }

  getCurrentDirection(): 'left' | 'right' | 'up' | 'down' | 'none' {
    return this.currentDirection;
  }

  resetPosition(): void {
    this.tileX = this.mapData.pacmanSpawn.x;
    this.tileY = this.mapData.pacmanSpawn.y;
    this.pixelX = tileToPixelX(this.tileX);
    this.pixelY = tileToPixelY(this.tileY);
    this.targetPixelX = this.pixelX;
    this.targetPixelY = this.pixelY;
    this.currentDirection = 'none';
    this.nextDirection = 'none';
    this.moving = false;
    this.prevTileX = this.tileX;
    this.prevTileY = this.tileY;
    this.isTunneling = false;
    this.draw();
  }

  update(_time: number, delta: number): void {
    this.updateMouth(delta);

    if (!this.moving) {
      this.tryMove();
    }

    if (this.moving) {
      this.moveTowardTarget(delta);
    }

    this.draw();
  }

  private updateMouth(delta: number): void {
    const isMoving = this.currentDirection !== 'none' || this.moving;
    const speed = isMoving ? MOUTH_ANIM_SPEED : MOUTH_IDLE_SPEED;
    this.mouthPhase += (delta / 1000) * speed;
  }

  /**
   * Returns the current mouth opening as a value between 0.0 (closed) and 1.0 (fully open).
   * Uses abs(sin) for a smooth continuous oscillation with no sudden jumps.
   */
  private getMouthOpenAmount(): number {
    return Math.abs(Math.sin(this.mouthPhase));
  }

  private tryMove(): void {
    if (this.nextDirection !== 'none') {
      const delta = DIRECTION_DELTAS[this.nextDirection];
      const nextTileX = this.tileX + delta.dx;
      const nextTileY = this.tileY + delta.dy;

      if (this.canMoveTo(nextTileX, nextTileY)) {
        this.currentDirection = this.nextDirection;
        this.startMoveTo(nextTileX, nextTileY);
        return;
      }
    }

    if (this.currentDirection !== 'none') {
      const delta = DIRECTION_DELTAS[this.currentDirection];
      const nextTileX = this.tileX + delta.dx;
      const nextTileY = this.tileY + delta.dy;

      if (this.canMoveTo(nextTileX, nextTileY)) {
        this.startMoveTo(nextTileX, nextTileY);
      }
    }
  }

  private canMoveTo(tileX: number, tileY: number): boolean {
    // Handle tunnel wrapping
    if (tileX < 0 || tileX >= this.mapData.width) {
      return this.isTunnelRow(this.tileY);
    }
    return isWalkable(this.mapData, tileX, tileY);
  }

  private isTunnelRow(tileY: number): boolean {
    return this.mapData.tunnelPairs.some(
      (pair) => pair.entry.y === tileY || pair.exit.y === tileY,
    );
  }

  private startMoveTo(tileX: number, tileY: number): void {
    // Save source tile before updating target
    this.prevTileX = this.tileX;
    this.prevTileY = this.tileY;

    // Handle tunnel wrapping with smooth slide-in
    if (tileX < 0) {
      const destTileX = this.mapData.width - 1;
      this.tileX = destTileX;
      this.tileY = tileY;
      // Place pixel one tile off-screen right and animate slide-in
      this.pixelX = tileToPixelX(this.mapData.width);
      this.targetPixelX = tileToPixelX(destTileX);
      this.targetPixelY = tileToPixelY(tileY);
      this.moving = true;
      this.isTunneling = true;
      return;
    }
    if (tileX >= this.mapData.width) {
      const destTileX = 0;
      this.tileX = destTileX;
      this.tileY = tileY;
      // Place pixel one tile off-screen left and animate slide-in
      this.pixelX = tileToPixelX(-1);
      this.targetPixelX = tileToPixelX(destTileX);
      this.targetPixelY = tileToPixelY(tileY);
      this.moving = true;
      this.isTunneling = true;
      return;
    }

    this.tileX = tileX;
    this.tileY = tileY;
    this.targetPixelX = tileToPixelX(tileX);
    this.targetPixelY = tileToPixelY(tileY);
    this.moving = true;
    this.isTunneling = false;
  }

  private moveTowardTarget(delta: number): void {
    // Check for queued direction changes while moving (skip during tunnel transitions)
    if (!this.isTunneling && this.nextDirection !== 'none' && this.nextDirection !== this.currentDirection) {
      if (this.handleMidMoveDirectionChange()) {
        return;
      }
    }

    // Normal movement toward target
    const speed = MOVE_SPEED * this.mapData.speedMultiplier * this.globalSpeedMultiplier * (delta / 1000);
    const dx = this.targetPixelX - this.pixelX;
    const dy = this.targetPixelY - this.pixelY;
    const dist = Math.sqrt(dx * dx + dy * dy);

    if (dist <= speed) {
      this.pixelX = this.targetPixelX;
      this.pixelY = this.targetPixelY;
      this.moving = false;
      this.isTunneling = false;
    } else {
      this.pixelX += (dx / dist) * speed;
      this.pixelY += (dy / dist) * speed;
    }
  }

  /**
   * Handle direction changes while Pac-Man is mid-move between tiles.
   * Supports: immediate reversal, pre-turn snapping, and corner recovery.
   * Returns true if direction was changed (caller should skip normal movement).
   */
  private handleMidMoveDirectionChange(): boolean {
    const queuedDir = this.nextDirection;
    if (queuedDir === 'none') {
      return false;
    }

    // 1. Immediate reversal for opposite direction
    if (this.isOppositeDirection(queuedDir, this.currentDirection)) {
      this.reverseDirection();
      return false; // Fall through to normal movement with new target
    }

    // 2. Pre-turn: close to target tile, queued turn is valid there
    const distToTarget = this.distanceToTarget();
    if (distToTarget <= PRE_TURN_TOLERANCE_PX) {
      const d = DIRECTION_DELTAS[queuedDir];
      const nextX = this.tileX + d.dx;
      const nextY = this.tileY + d.dy;
      if (this.canMoveTo(nextX, nextY)) {
        // Snap to target tile center and immediately start turning
        this.pixelX = this.targetPixelX;
        this.pixelY = this.targetPixelY;
        this.currentDirection = queuedDir;
        this.startMoveTo(nextX, nextY);
        return true;
      }
    }

    // 3. Corner recovery: just barely left source tile, turn valid at source
    const distToSource = this.distanceToSource();
    if (distToSource <= CORNER_RECOVERY_PX) {
      const d = DIRECTION_DELTAS[queuedDir];
      const nextX = this.prevTileX + d.dx;
      const nextY = this.prevTileY + d.dy;
      if (this.canMoveTo(nextX, nextY)) {
        // Snap back to source tile center and turn
        this.pixelX = tileToPixelX(this.prevTileX);
        this.pixelY = tileToPixelY(this.prevTileY);
        this.tileX = this.prevTileX;
        this.tileY = this.prevTileY;
        this.currentDirection = queuedDir;
        this.startMoveTo(nextX, nextY);
        return true;
      }
    }

    return false;
  }

  private reverseDirection(): void {
    this.currentDirection = this.nextDirection;

    // Swap source and target tiles
    const tempTileX = this.tileX;
    const tempTileY = this.tileY;
    this.tileX = this.prevTileX;
    this.tileY = this.prevTileY;
    this.prevTileX = tempTileX;
    this.prevTileY = tempTileY;

    // Update pixel target to new destination (old source)
    this.targetPixelX = tileToPixelX(this.tileX);
    this.targetPixelY = tileToPixelY(this.tileY);
    // pixelX/pixelY stay the same — Pac-Man is mid-tile
  }

  private isOppositeDirection(dir1: Direction, dir2: Direction): boolean {
    if (dir1 === 'none' || dir2 === 'none') {
      return false;
    }
    return (
      (dir1 === 'left' && dir2 === 'right') ||
      (dir1 === 'right' && dir2 === 'left') ||
      (dir1 === 'up' && dir2 === 'down') ||
      (dir1 === 'down' && dir2 === 'up')
    );
  }

  private distanceToTarget(): number {
    const dx = this.targetPixelX - this.pixelX;
    const dy = this.targetPixelY - this.pixelY;
    return Math.sqrt(dx * dx + dy * dy);
  }

  private distanceToSource(): number {
    const sourcePixelX = tileToPixelX(this.prevTileX);
    const sourcePixelY = tileToPixelY(this.prevTileY);
    const dx = sourcePixelX - this.pixelX;
    const dy = sourcePixelY - this.pixelY;
    return Math.sqrt(dx * dx + dy * dy);
  }

  private draw(): void {
    this.graphics.clear();

    // Outer neon glow
    this.graphics.fillStyle(COLORS.PACMAN_GLOW, 0.15);
    this.graphics.fillCircle(this.pixelX, this.pixelY, PACMAN_RADIUS + 4);
    this.graphics.fillStyle(COLORS.PACMAN_GLOW, 0.08);
    this.graphics.fillCircle(this.pixelX, this.pixelY, PACMAN_RADIUS + 7);

    // Main body
    this.graphics.fillStyle(COLORS.PACMAN, 1);

    const openAmount = this.getMouthOpenAmount();
    // Smoothly interpolate mouth gap from 0 (closed circle) to MAX_MOUTH_ANGLE
    const halfGap = openAmount * (MAX_MOUTH_ANGLE / 2);

    if (halfGap > 0.01) {
      // Draw pac-man as an arc with a smooth mouth gap
      const facing = this.getStartAngle();
      // Arc starts after the gap and sweeps around
      const arcStart = facing + halfGap;
      const arcEnd = facing - halfGap + Math.PI * 2;

      this.graphics.beginPath();
      this.graphics.moveTo(this.pixelX, this.pixelY);
      this.graphics.arc(
        this.pixelX,
        this.pixelY,
        PACMAN_RADIUS,
        arcStart,
        arcEnd,
      );
      this.graphics.closePath();
      this.graphics.fillPath();
    } else {
      // Mouth essentially closed — draw full circle
      this.graphics.fillCircle(this.pixelX, this.pixelY, PACMAN_RADIUS);
    }
  }

  private getStartAngle(): number {
    switch (this.currentDirection) {
      case 'right': return 0;
      case 'down': return Math.PI / 2;
      case 'left': return Math.PI;
      case 'up': return -Math.PI / 2;
      default: return 0;
    }
  }

  startDeathAnimation(): void {
    this.deathAnimationActive = true;
    this.deathAnimationProgress = 0;
    this.graphics.setVisible(true);
  }

  isDeathAnimationComplete(): boolean {
    return this.deathAnimationActive && this.deathAnimationProgress >= 1;
  }

  updateDeathAnimation(delta: number): void {
    if (!this.deathAnimationActive) {
      return;
    }

    // Animation takes about 1 second (1000ms)
    this.deathAnimationProgress += delta / 1000;
    if (this.deathAnimationProgress > 1) {
      this.deathAnimationProgress = 1;
    }

    this.drawDeathFrame();
  }

  stopDeathAnimation(): void {
    this.deathAnimationActive = false;
    this.deathAnimationProgress = 0;
  }

  private drawDeathFrame(): void {
    this.graphics.clear();

    const progress = this.deathAnimationProgress;

    if (progress >= 1) {
      // Fully dissolved - draw nothing
      return;
    }

    // Phase 1 (0-0.6): Mouth opens wider and wider (classic dissolve)
    // Phase 2 (0.6-1.0): Shrink and fade
    this.graphics.fillStyle(COLORS.PACMAN, 1 - progress * 0.5);

    if (progress < 0.6) {
      // Mouth opens progressively from current angle to full circle
      const mouthOpenAmount = progress / 0.6;
      const gapAngle = Math.PI / 6 + mouthOpenAmount * (Math.PI - Math.PI / 6);
      const startAngle = Math.PI * 1.5 + gapAngle;
      const endAngle = Math.PI * 1.5 - gapAngle;

      this.graphics.beginPath();
      this.graphics.moveTo(this.pixelX, this.pixelY);
      this.graphics.arc(
        this.pixelX,
        this.pixelY,
        PACMAN_RADIUS,
        startAngle,
        endAngle,
      );
      this.graphics.closePath();
      this.graphics.fillPath();
    } else {
      // Shrinking phase
      const shrinkProgress = (progress - 0.6) / 0.4;
      const radius = PACMAN_RADIUS * (1 - shrinkProgress);
      if (radius > 0.5) {
        this.graphics.fillCircle(this.pixelX, this.pixelY, radius);
      }
    }
  }

  setVisible(visible: boolean): void {
    this.graphics.setVisible(visible);
  }

  setScale(scale: number): void {
    this.graphics.setScale(scale);
  }

  setAlpha(alpha: number): void {
    this.graphics.setAlpha(alpha);
  }

  getGraphics(): Phaser.GameObjects.Graphics {
    return this.graphics;
  }

  destroy(): void {
    this.graphics.destroy();
  }
}
